import format from '@/common/utilities/format';

/**
 * Formats Opendoor valueEstimate lower/upper as "$784K-$884K", or '' when missing/invalid.
 * @param {{ lower?: number, upper?: number }} valueEstimate
 * @returns {string}
 */
export function formatOpendoorEstimateRange(valueEstimate) {
    if (!valueEstimate || valueEstimate.lower == null || valueEstimate.upper == null) {
        return '';
    }
    const lower = Number(valueEstimate.lower);
    const upper = Number(valueEstimate.upper);
    if (Number.isNaN(lower) || Number.isNaN(upper)) {
        return '';
    }
    const lowerK = `$${format.friendlyPrice(lower)}`;
    const upperK = `$${format.friendlyPrice(upper)}`;
    return `${lowerK}-${upperK}`;
}

export function buildOpendoorAddressFromGeo(geo, options = {}) {
    const includeStreet2 = options.includeStreet2 !== false;
    const g = geo || {};
    const state = (g.stateCode || g.state || '').toString().toUpperCase().slice(0, 2);
    const street1 = (g.address || '').trim() || (g.formatAddress || '').split(',')[0]?.trim() || '';

    if (!street1 || !g.city || !state || !g.zipcode) {
        return null;
    }

    const address = {
        street1,
        city: g.city,
        state,
        postalCode: String(g.zipcode),
    };

    if (includeStreet2 && g.subPremise) {
        address.street2 = g.subPremise;
    }

    return address;
}

export function getPreferredOpendoorHomeSellProduct(opendoorDetails) {
    const homeSell = opendoorDetails && opendoorDetails.products && Array.isArray(opendoorDetails.products.homeSell) ? opendoorDetails.products.homeSell : [];
    if (!homeSell.length) {
        throw new Error('no avaliable data');
    }
    const eligibleItem = homeSell.find((item) => item && item.isEligible === true);
    if (eligibleItem) {
        return eligibleItem;
    }
    const firstItem = homeSell[0];
    if (firstItem && firstItem.denialExplanation) {
        return firstItem;
    }
    throw new Error('no avaliable data');
}

export function logOpendoorAddressDetailsException(message, details = {}) {
    if (typeof window === 'undefined' || typeof window.sendNodeLog !== 'function') {
        return;
    }
    try {
        window.sendNodeLog(message || 'Opendoor', window.location.href, 0, details);
    } catch (e) {
        // ignore logging errors
    }
}

/**
 * Opendoor range midpoint: (low+high)/2; if only one bound exists, use that bound.
 * @param {{ lower?: number, upper?: number }} valueEstimate
 * @returns {number|null}
 */
export function getOpendoorValueEstimateCenter(valueEstimate) {
    if (!valueEstimate || typeof valueEstimate !== 'object') {
        return null;
    }
    const lo = valueEstimate.lower;
    const hi = valueEstimate.upper;
    const hasLo = lo != null && lo !== '' && Number.isFinite(Number(lo));
    const hasHi = hi != null && hi !== '' && Number.isFinite(Number(hi));
    if (hasLo && hasHi) {
        return (Number(lo) + Number(hi)) / 2;
    }
    if (hasLo) {
        return Number(lo);
    }
    if (hasHi) {
        return Number(hi);
    }
    return null;
}

/**
 * OD vs Movoto headline value as a percentage, floored to the nearest 5 (e.g. 109% → 105).
 * @param {number} odCenter
 * @param {number} denominator
 * @returns {number|null}
 */
export function bucketOpendoorEstimateRatioPercent(odCenter, denominator) {
    if (odCenter == null || !Number.isFinite(odCenter) || denominator == null || !Number.isFinite(denominator) || denominator <= 0) {
        return null;
    }
    const pct = (odCenter / denominator) * 100;
    return Math.floor(pct / 5) * 5;
}

/**
 * Which value is shown as the primary bold price on DPP — mirrors property-summary-title.vue
 * (recent sale: sold price in the hero when present; older sale: Movoto estimate when present).
 * Active listings are 'none' because the hero line is list price, not the Movoto estimate headline.
 * @param {object} dpp
 * @param {{ value?: number }|null|undefined} estimateInfo from getEstimateInfo
 * @returns {'M'|'S'|'none'}
 */
export function getDppSummaryPrimaryPriceKindForOpendoorGa(dpp, estimateInfo) {
    if (!dpp || dpp.isRentals) {
        return 'none';
    }
    if (dpp.isActive) {
        return 'none';
    }
    const hasEst = estimateInfo && estimateInfo.value > 0;
    const hasPrice = !!dpp.price;
    const soldWithin12Mo = dpp.soldDate && +new Date(dpp.soldDate) + 31557600000 > +new Date();
    if (soldWithin12Mo) {
        if (hasPrice) {
            return 'S';
        }
        if (hasEst) {
            return 'M';
        }
        return 'none';
    }
    if (hasEst) {
        return 'M';
    }
    if (hasPrice) {
        return 'S';
    }
    return 'none';
}

/**
 * @param {object} dpp
 * @param {{ value?: number }|null|undefined} estimateInfo
 * @param {number|null} odCenter
 * @returns {string}
 */
export function buildOpendoorAcceptedWithEstimateGaLabel(dpp, estimateInfo, odCenter) {
    if (odCenter == null || !Number.isFinite(odCenter)) {
        return 'accepted_with_estimate';
    }
    const kind = getDppSummaryPrimaryPriceKindForOpendoorGa(dpp, estimateInfo);
    if (kind === 'M') {
        const mEst = estimateInfo && estimateInfo.value > 0 ? estimateInfo.value : null;
        const x = bucketOpendoorEstimateRatioPercent(odCenter, mEst);
        if (x == null) {
            return 'accepted_with_estimate';
        }
        return `accepted_with_estimate_M_${x}`;
    }
    if (kind === 'S') {
        const rawSold = dpp && dpp.price != null ? Number(dpp.price) : NaN;
        const sold = Number.isFinite(rawSold) && rawSold > 0 ? rawSold : null;
        const x = bucketOpendoorEstimateRatioPercent(odCenter, sold);
        if (x == null) {
            return 'accepted_with_estimate';
        }
        return `accepted_with_estimate_S_${x}`;
    }
    return 'accepted_with_estimate';
}
